import React from "react";

import TeachingMaterialSidebar from "../../../organisms/student/TeachingMaterialSidebar";
import BaWorkSection from "../../../organisms/BaWorkSection";
import LessonAccordion from "../../../organisms/LessonAccordion";
import Spinner from "../../../atoms/Spinner";
import {
  ILessonByTeachingMaterial,
  ITeachingMaterial,
  IActionList,
  OnePagePortfolioAssessments,
} from "../../../state";
import {
  TEACHING_MATERIAL_KEYWORDS,
  CategoryType,
} from "../../../constants/teaching_materials";

import IssueList from "./IssueList";
import styles from "./DataAnswersList.module.scss";

export type DataAnswersListProps = {
  teaching_materials: ITeachingMaterial[];
  listLessons: ILessonByTeachingMaterial[];
  calling: boolean;
  currentStudentId: number;
  teachingMaterialId: number;
  portfolio: OnePagePortfolioAssessments[] | null;
  actionList: IActionList;
  onChangeTeachingMaterial: (id: number) => void;
  handleComment: (
    answerId: number,
    content: string,
    commentId?: number,
  ) => void;
  handleDeleteComment: (answerId: number, commentId: number) => void;
};

const DataAnswersList: React.FC<DataAnswersListProps> = ({
  teaching_materials,
  listLessons,
  calling,
  currentStudentId,
  teachingMaterialId,
  portfolio,
  actionList,
  onChangeTeachingMaterial,
  handleComment,
  handleDeleteComment,
}) => {
  const currentActionLessons = React.useMemo(() => {
    const tm = actionList.teaching_materials.find(
      t => t.id === teachingMaterialId,
    );
    return tm?.lessons ?? [];
  }, [actionList, teachingMaterialId]);

  const mountedRef = React.useRef(false);
  const lessonsReadyRef = React.useRef(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!mountedRef.current) return;
    lessonsReadyRef.current = false;
    setHasLoaded(false);
  }, [teachingMaterialId]);

  React.useEffect(() => {
    if (!mountedRef.current) return;
    lessonsReadyRef.current = true;
    if (!calling) setHasLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listLessons]);

  React.useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (lessonsReadyRef.current && !calling) setHasLoaded(true);
  }, [calling]);

  const [userSelectedTab, setUserSelectedTab] = React.useState<CategoryType>(
    "middle",
  );

  const categorizedData = React.useMemo(() => {
    const teachingMaterialsExcludedOrientation = teaching_materials.filter(
      tm => !tm.title.includes(TEACHING_MATERIAL_KEYWORDS.ORIENTATION),
    );
    const middle = teachingMaterialsExcludedOrientation.filter(tm => {
      const k = tm.kind || "";
      return (
        k.includes(TEACHING_MATERIAL_KEYWORDS.CORE) &&
        tm.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE)
      );
    });
    const high = teachingMaterialsExcludedOrientation.filter(tm => {
      const k = tm.kind || "";
      return (
        k.includes(TEACHING_MATERIAL_KEYWORDS.CORE) &&
        tm.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH)
      );
    });
    const core = teachingMaterialsExcludedOrientation.filter(tm => {
      const k = tm.kind || "";
      return (
        k.includes(TEACHING_MATERIAL_KEYWORDS.CORE) &&
        !tm.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE) &&
        !tm.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH)
      );
    });
    const base = teachingMaterialsExcludedOrientation.filter(tm => {
      const k = tm.kind || "";
      return k.includes(TEACHING_MATERIAL_KEYWORDS.BASE);
    });
    const showTabs =
      [middle, high, core, base].filter(a => a.length > 0).length > 1;
    return { middle, high, core, base, showTabs };
  }, [teaching_materials]);

  const activeTab = React.useMemo(() => {
    const categories: CategoryType[] = ["middle", "high", "core", "base"];
    if (
      categories.includes(userSelectedTab) &&
      categorizedData[userSelectedTab].length > 0
    ) {
      return userSelectedTab;
    }
    return categories.find(key => categorizedData[key].length > 0) ?? "middle";
  }, [userSelectedTab, categorizedData]);

  const currentMaterials = React.useMemo(() => {
    const categories: CategoryType[] = ["middle", "high", "core", "base"];
    if (!categorizedData.showTabs) {
      const key = categories.find(k => categorizedData[k].length > 0);
      return key ? categorizedData[key] : [];
    }
    return categorizedData[activeTab] ?? [];
  }, [categorizedData, activeTab]);

  const currentMaterial = React.useMemo(
    () => currentMaterials.find(m => m.id === teachingMaterialId),
    [currentMaterials, teachingMaterialId],
  );

  React.useEffect(() => {
    if (currentMaterials.length === 0) return;
    const isCurrentInTab = currentMaterials.some(
      m => m.id === teachingMaterialId,
    );
    if (!isCurrentInTab) {
      onChangeTeachingMaterial(currentMaterials[0].id);
    }
  }, [teachingMaterialId, currentMaterials, onChangeTeachingMaterial]);

  const isMiddleOrHigh = React.useMemo(() => {
    const current = currentMaterials.find(m => m.id === teachingMaterialId);
    if (!current) return false;
    const k = current.kind || "";
    return (
      k.includes(TEACHING_MATERIAL_KEYWORDS.CORE) &&
      (current.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE) ||
        current.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH))
    );
  }, [currentMaterials, teachingMaterialId]);

  return (
    <>
      {teaching_materials.length === 0 ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
        </div>
      ) : (
        <div className={styles.pageLayout}>
          <TeachingMaterialSidebar
            currentMaterials={currentMaterials}
            viewingMaterialId={teachingMaterialId || null}
            activeModalId={null}
            activeTab={activeTab}
            showTabs={categorizedData.showTabs}
            hasMiddle={categorizedData.middle.length > 0}
            hasHigh={categorizedData.high.length > 0}
            hasCore={categorizedData.core.length > 0}
            hasBase={categorizedData.base.length > 0}
            onTabClick={tab => setUserSelectedTab(tab)}
            onItemClick={(_e, materialId) =>
              onChangeTeachingMaterial(materialId)
            }
          />
          <div className={styles.main}>
            {!hasLoaded ? (
              <div className={styles.loading}>
                <Spinner spinColor="black" />
              </div>
            ) : (
              <div className={styles.innerContent}>
                {currentMaterial && (
                  <div className={styles.materialHeader}>
                    <span className={styles.materialVol}>
                      {currentMaterial.name}
                    </span>
                    <span className={styles.materialTitle}>
                      {currentMaterial.title.replace(
                        /\s*-高校-|\s*-中学-/g,
                        "",
                      )}
                    </span>
                  </div>
                )}
                <BaWorkSection key={teachingMaterialId} oppas={portfolio} />
                {listLessons.map(lesson => {
                  const isLeaf = lesson.title.includes("実践ワークシート");
                  return (
                    <LessonAccordion
                      key={`${lesson.id}-${teachingMaterialId}`}
                      lesson={lesson}
                      isLeaf={isLeaf}
                      actionLesson={currentActionLessons.find(
                        l => l.id === lesson.id,
                      )}
                    >
                      <IssueList
                        issues={lesson.issues}
                        isLeaf={isLeaf}
                        isMiddleOrHigh={isMiddleOrHigh}
                        calling={calling}
                        currentStudentId={currentStudentId}
                        handleComment={handleComment}
                        handleDeleteComment={handleDeleteComment}
                      />
                    </LessonAccordion>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DataAnswersList;
